<template>
  <div class="current-url-manage">
    <!-- 操作欄 -->
    <div class="action-bar">
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openDialog()">新增配置</el-button>
    </div>

    <!-- 配置列表 -->
    <el-table v-loading="loading" :data="filteredData" border stripe style="width: 100%" size="small">
      <el-table-column prop="url" label="頁面路徑" width="160"></el-table-column>
      <el-table-column prop="brand" label="品牌" width="120" align="center">
        <template slot-scope="scope">
          <el-tag size="small" :type="brandTagType(scope.row.brand)">{{ scope.row.brand }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="當前活動" min-width="200">
        <template slot-scope="scope">
          <span>{{ getActivityTitle(scope.row.curAcId) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" align="center">
        <template slot-scope="scope">
          <el-button type="text" size="small" icon="el-icon-edit" @click="openDialog(scope.row)">編輯</el-button>
          <el-button type="text" size="small" icon="el-icon-delete" class="danger-btn" @click="handleDelete(scope.row)">
            刪除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 新增/編輯彈窗 -->
    <el-dialog
      :title="isEdit ? '編輯配置' : '新增配置'"
      :visible.sync="dialogVisible"
      width="500px"
      custom-class="url-dialog"
      :close-on-click-modal="false"
    >
      <el-form ref="urlForm" :model="form" :rules="formRules" label-width="100px" size="small">
        <el-form-item label="頁面路徑" prop="url">
          <el-input v-model="form.url" placeholder="如 /zeekr、/icaur、/hongqi" maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="品牌" prop="brand">
          <el-select v-model="form.brand" placeholder="請選擇品牌" style="width: 100%">
            <el-option label="HONGQI" value="hongqi"></el-option>
            <el-option label="ZEEKR" value="zeekr"></el-option>
            <el-option label="ICAUR" value="icaur"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="當前活動" prop="curAcId">
          <el-select v-model="form.curAcId" placeholder="請選擇活動" style="width: 100%" filterable>
            <el-option
              v-for="item in availableActivityOptions"
              :key="item.id"
              :label="item.titleAc"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="handleSubmit">確 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  currentUrlListApi,
  currentUrlAddApi,
  currentUrlUpdateApi,
  currentUrlDeleteApi,
  activityListApi,
} from '@/Api/requestApi'

export default {
  name: 'CurrentUrlManage',
  props: {
    brand: {
      type: String,
      default: 'icaur',
    },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      tableData: [],
      activityOptions: [],
      dialogVisible: false,
      isEdit: false,
      editId: null,
      form: {
        url: '',
        brand: '',
        curAcId: '',
      },
      formRules: {
        url: [{ required: true, message: '請輸入頁面路徑', trigger: 'blur' }],
        brand: [{ required: true, message: '請選擇品牌', trigger: 'change' }],
        curAcId: [{ required: true, message: '請選擇當前活動', trigger: 'change' }],
      },
    }
  },
  computed: {
    filteredData() {
      if (!this.brand) return this.tableData
      return this.tableData.filter((item) => item.brand === this.brand)
    },
    availableActivityOptions() {
      // 已按當前品牌過濾；表單中品牌變化時二次過濾
      const targetBrand = this.form.brand || this.brand
      return this.activityOptions.filter((a) => !a.brand || a.brand === targetBrand)
    },
  },
  watch: {
    brand() {
      this.fetchData()
      this.fetchActivities()
    },
    'form.brand'(newVal, oldVal) {
      // 編輯/新增打開彈窗時不觸發清空
      if (this._skipBrandWatch) return
      if (oldVal && newVal !== oldVal) {
        this.form.curAcId = ''
      }
    },
  },
  mounted() {
    this.fetchData()
    this.fetchActivities()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const {
          data: { code, data },
        } = await currentUrlListApi({ brand: this.brand })
        if (code === 200) {
          this.tableData = data || []
        }
      } catch (err) {
        console.error('獲取活動配置失敗:', err)
      } finally {
        this.loading = false
      }
    },
    async fetchActivities() {
      try {
        const {
          data: { code, data },
        } = await activityListApi({ brand: this.brand })
        if (code === 200) {
          this.activityOptions = data || []
        }
      } catch (err) {
        console.error('獲取活動列表失敗:', err)
      }
    },
    getActivityTitle(acId) {
      const found = this.activityOptions.find((a) => a.id === acId)
      return found ? found.titleAc : acId || '未設置'
    },
    openDialog(row) {
      this.isEdit = !!row
      this._skipBrandWatch = true
      if (row) {
        this.editId = row.id
        this.form = {
          url: row.url || '',
          brand: row.brand || this.brand,
          curAcId: row.curAcId || '',
        }
      } else {
        this.editId = null
        this.form = {
          url: '',
          brand: this.brand,
          curAcId: '',
        }
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this._skipBrandWatch = false
        this.$refs.urlForm && this.$refs.urlForm.clearValidate()
      })
    },
    async handleSubmit() {
      this.$refs.urlForm.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = { ...this.form }
          let res
          if (this.isEdit) {
            res = await currentUrlUpdateApi({ id: this.editId, ...payload })
          } else {
            res = await currentUrlAddApi(payload)
          }
          const { code, message } = res.data
          if (code === 200) {
            this.$message({ message: this.isEdit ? '修改成功' : '創建成功', type: 'success' })
            this.dialogVisible = false
            this.fetchData()
          } else {
            this.$message({ message: message || '操作失敗', type: 'error' })
          }
        } catch (err) {
          console.error('提交失敗:', err)
          this.$message({ message: '提交失敗', type: 'error' })
        } finally {
          this.submitting = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm(`確定要刪除「${row.url}」的配置嗎？`, '提示', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          try {
            const {
              data: { code, message },
            } = await currentUrlDeleteApi({ id: row.id })
            if (code === 200) {
              this.$message({ message: '刪除成功', type: 'success' })
              this.fetchData()
            } else {
              this.$message({ message: message || '刪除失敗', type: 'error' })
            }
          } catch (err) {
            console.error('刪除失敗:', err)
            this.$message({ message: '刪除失敗', type: 'error' })
          }
        })
        .catch(() => {})
    },
    brandTagType(brand) {
      const map = { hongqi: 'danger', zeekr: '', icaur: 'warning' }
      return map[brand] || 'info'
    },
  },
}
</script>

<style lang="scss" scoped>
.current-url-manage {
  .action-bar {
    margin-bottom: 16px;
  }

  .danger-btn {
    color: #f56c6c;
  }
}

/* 手機端適配 */
@media (max-width: 768px) {
  .action-bar {
    ::v-deep .el-button {
      width: 100%;
    }
  }

  ::v-deep .url-dialog {
    width: 94% !important;

    /* 表單欄目標題佔一行、輸入框佔一行 */
    .el-form-item__label {
      float: none;
      display: block;
      width: 100% !important;
      text-align: left;
      padding: 0 0 6px;
      line-height: 1.4;
    }

    .el-form-item__content {
      margin-left: 0 !important;
    }
  }
}
</style>
